import React, { useState } from "react";
import { metadataForSave } from "../../utils/folders/metadataForSave.ts";
import {
  ClusterOutlined,
  MailFilled,
  PhoneFilled,
  UserOutlined,
} from "@ant-design/icons";
import { Folder } from "../../redux/interfaces/folder.types.ts";
import { Col, Divider, Row, Button } from "antd";
import { darkBlue, gray, green, orange } from "../../utils/theme/colors.ts";
import { ParticipantFormModal } from "./ParticipantFormModal";
import { message } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { updateFolder } from "../../redux/actions/folderActions";
import { RootState } from "../../redux/store.ts";

interface Props {
  folder: Folder;
}

export const CardHeader: React.FC<Props> = ({ folder: initialFolder }) => {
  const dispatch = useAppDispatch();

  const updatedFolder =
    useAppSelector((state: RootState) =>
      state.folder.results.find((f) => f.id === initialFolder.id),
    ) || initialFolder;

  const metadata = updatedFolder.metadata;
  const tenant = metadata?.tenant;
  const advisor = metadata?.advisor;
  const guarantor = metadata?.guarantor;
  const landlord = metadata?.landlord;
  const organization = metadata?.organization;

  const [modalConfig, setModalConfig] = useState<{
    isOpen: boolean;
    participantType: "advisor" | "tenant" | "guarantor" | "landlord";
    isEdit: boolean;
    initialData?: any;
  }>({
    isOpen: false,
    participantType: "advisor",
    isEdit: false,
    initialData: null,
  });

  const handleEditParticipant = (
    participantType: "advisor" | "tenant" | "guarantor" | "landlord",
    initialData?: any,
  ) => {
    let participantData = null;
    if (initialData) {
      participantData = updatedFolder.metadata?.[participantType] || null; // Changed from folder to updatedFolder
    }

    setModalConfig({
      isOpen: true,
      participantType,
      isEdit: !!initialData,
      initialData: participantData,
    });
  };

  const handleParticipantSubmit = async (values: any) => {
    const participantData =
      values.metadata?.[modalConfig.participantType] || values;

    try {
      const updatedMetadata = {
        metadata: {
          ...metadataForSave(updatedFolder.metadata),
          [modalConfig.participantType]: participantData,
        },
      };

      await dispatch(
        updateFolder({
          folderId: updatedFolder.id,
          data: updatedMetadata,
        }),
      ).unwrap();

      message.success("Información actualizada exitosamente");
      setModalConfig((prev) => ({ ...prev, isOpen: false }));
    } catch (error) {
      message.error("Error al actualizar la información");
      console.error("Update error:", error);
    }
  };

  const GUARANTOR_TEMPLATES = [
    "guarantor-pf",
    "guarantor-pf-cja",
    "guarantor-pm",
    "guarantor-pm-cja",
    "guarantor-investigation-pf",
    "guarantor-investigation-pm",
  ];
  const LANDLORD_TEMPLATES = ["lease-contract-pf", "lease-contract-pm"];

  const hasTemplate = (folder: Folder, templateSlugs: string[]) => {
    // Check direct templates array first
    if (folder.templates && folder.templates.length > 0) {
      return folder.templates.some((template) =>
        templateSlugs.includes(template.slug),
      );
    }

    // If no direct templates, check subfolders
    if (folder.subfolders && folder.subfolders.length > 0) {
      return folder.subfolders.some((subfolder) =>
        templateSlugs.includes(subfolder.template?.slug ?? ""),
      );
    }

    return false;
  };

  return (
    <>
      <div className="w-full flex flex-col justify-center items-center text-sm font-medium text-gray-800">
        {organization && (
          <div className="flex mb-1">
            <ClusterOutlined className="w-5 h-5" style={{ color: "black" }} />
            <div>
              <b>Organización:</b> {organization.name}
            </div>
          </div>
        )}

        <Row className="w-full flex flex-wrap break-all">
          <Divider />

          {/* Advisor Section */}
          <Col className="mr-4">
            <div
              className="hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors"
              onClick={() =>
                handleEditParticipant("advisor", metadata?.advisor)
              }
            >
              {advisor?.full_name && (
                <div className="flex mb-1">
                  <UserOutlined className="w-5 h-5" style={{ color: green }} />
                  <div>
                    <b>Asesor:</b> {advisor.full_name}
                  </div>
                </div>
              )}
              {advisor?.email && (
                <div className="flex mb-1">
                  <MailFilled className="w-5 h-5" style={{ color: orange }} />
                  <div>
                    <b>Correo:</b> {advisor.email}
                  </div>
                </div>
              )}
              {advisor?.phone_number && (
                <div className="flex mb-1">
                  <PhoneFilled className="w-5 h-5" style={{ color: gray }} />
                  <div>
                    <b>Teléfono:</b> {advisor.phone_number}
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Tenant Section */}
          <Col className="mr-4">
            <div
              className="hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors"
              onClick={() => handleEditParticipant("tenant", metadata?.tenant)}
            >
              {tenant?.full_name && (
                <div className="flex mb-1">
                  <UserOutlined
                    className="w-5 h-5"
                    style={{ color: darkBlue }}
                  />
                  <div>
                    <b>Inquilino:</b> {tenant.full_name}
                  </div>
                </div>
              )}
              {tenant?.email && (
                <div className="flex mb-1">
                  <MailFilled className="w-5 h-5" style={{ color: orange }} />
                  <div>
                    <b>Correo:</b> {tenant.email}
                  </div>
                </div>
              )}
              {tenant?.phone_number && (
                <div className="flex mb-1">
                  <PhoneFilled className="w-5 h-5" style={{ color: gray }} />
                  <div>
                    <b>Teléfono:</b> {tenant.phone_number}
                  </div>
                </div>
              )}
            </div>
          </Col>

          {/* Guarantor Section */}
          {hasTemplate(updatedFolder, GUARANTOR_TEMPLATES) && (
            <Col className="mr-4">
              {metadata?.guarantor ? (
                <div
                  className="hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors"
                  onClick={() =>
                    handleEditParticipant("guarantor", metadata?.guarantor)
                  }
                >
                  <div className="flex mb-1">
                    <UserOutlined
                      className="w-5 h-5"
                      style={{ color: darkBlue }}
                    />
                    <div>
                      <b>Fiador</b>
                      {guarantor?.name
                        ? `: ${guarantor.name} ${guarantor?.first_surname || ""} ${guarantor?.second_surname || ""}`.trim()
                        : ""}
                    </div>
                  </div>
                  {guarantor?.email && (
                    <div className="flex mb-1">
                      <MailFilled
                        className="w-5 h-5"
                        style={{ color: orange }}
                      />
                      <div>
                        <b>Correo:</b> {guarantor?.email}
                      </div>
                    </div>
                  )}
                  {guarantor?.phone_number && (
                    <div className="flex mb-1">
                      <PhoneFilled
                        className="w-5 h-5"
                        style={{ color: gray }}
                      />
                      <div>
                        <b>Teléfono:</b> {guarantor.phone_number}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  type="link"
                  onClick={() => handleEditParticipant("guarantor")}
                >
                  Agregar Fiador
                </Button>
              )}
            </Col>
          )}

          {/* Landlord Section */}
          {hasTemplate(updatedFolder, LANDLORD_TEMPLATES) && (
            <Col className="mr-4">
              {metadata?.landlord ? (
                <div
                  className="hover:bg-gray-50 p-2 rounded cursor-pointer transition-colors"
                  onClick={() =>
                    handleEditParticipant("landlord", metadata?.landlord)
                  }
                >
                  <div className="flex mb-1">
                    <UserOutlined
                      className="w-5 h-5"
                      style={{ color: darkBlue }}
                    />
                    <div>
                      <b>Propietario</b>
                      {landlord?.name
                        ? `: ${landlord.name} ${landlord?.first_surname || ""} ${landlord?.second_surname || ""}`.trim()
                        : ""}
                    </div>
                  </div>
                  {landlord?.email && (
                    <div className="flex mb-1">
                      <MailFilled
                        className="w-5 h-5"
                        style={{ color: orange }}
                      />
                      <div>
                        <b>Correo:</b> {landlord?.email}
                      </div>
                    </div>
                  )}
                  {landlord?.phone_number && (
                    <div className="flex mb-1">
                      <PhoneFilled
                        className="w-5 h-5"
                        style={{ color: gray }}
                      />
                      <div>
                        <b>Teléfono:</b> {landlord.phone_number}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Button
                  type="link"
                  onClick={() => handleEditParticipant("landlord")}
                >
                  Agregar Propietario
                </Button>
              )}
            </Col>
          )}

          <Divider />
        </Row>
      </div>

      <ParticipantFormModal
        {...modalConfig}
        onClose={() => setModalConfig((prev) => ({ ...prev, isOpen: false }))}
        onSubmit={handleParticipantSubmit}
        folder={updatedFolder}
      />
    </>
  );
};
